/**
 * AppViewport - Root container that handles the sliding app + coach sidebar
 * The entire app slides as one unit to reveal the coach sidebar
 */

import { ChevronLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useUserSettings } from '../api/hooks/useSettings'
import { cn } from '../lib/utils'
import { useCoachStore } from '../stores/coachStore'
import { CoachSidebar } from './coach/CoachSidebar'
import { UnifiedButton } from './ui/unified-button'

interface AppViewportProps {
  children: React.ReactNode
}

export const AppViewport = ({ children }: AppViewportProps) => {
  const { isCoachOpen, setIsCoachOpen } = useCoachStore()
  const [isMobile, setIsMobile] = useState(false)
  const { data: userSettings } = useUserSettings()
  
  // Check if AI Coach is enabled (default to true if not set)
  const isAICoachEnabled = userSettings?.settings?.ai?.enabled ?? true
  
  // Close coach sidebar if AI is disabled while it's open
  useEffect(() => {
    if (!isAICoachEnabled && isCoachOpen) {
      setIsCoachOpen(false)
    }
  }, [isAICoachEnabled, isCoachOpen, setIsCoachOpen])


  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768) // md breakpoint
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle shortcuts if AI Coach is enabled
      if (!isAICoachEnabled) return
      
      // Escape to close coach (but not with Shift)
      if (e.key === 'Escape' && !e.shiftKey && isCoachOpen) {
        e.preventDefault()
        setIsCoachOpen(false)
      }
      // Cmd/Ctrl + S to toggle coach
      if (e.key === 's' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setIsCoachOpen(!isCoachOpen)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isCoachOpen, setIsCoachOpen, isAICoachEnabled])

  // Prevent body scroll when coach is open
  useEffect(() => {
    if (isCoachOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isCoachOpen])

  const slideDistance = isMobile ? '100vw' : '480px'

  return (
    <div className="app-viewport fixed inset-0 overflow-hidden">
      {/* Toggle Button - Outside sliding container so it's always clickable */}
      {isAICoachEnabled && (
        <UnifiedButton
          variant="secondary"
          size="sm"
          onClick={() => setIsCoachOpen(!isCoachOpen)}
          className={cn(
            'fixed top-4 right-4 z-[100] h-10 w-10 rounded-full p-0 shadow-lg transition-all duration-100',
            isCoachOpen && 'opacity-0 pointer-events-none'
          )}
          data-coach-toggle
          title="Open Milo AI Coach - ⌘S to toggle, Esc to close"
        >
          <div className="flex size-full items-center justify-center">
            <ChevronLeft className={cn('mr-1 h-5 w-5')} />
          </div>
        </UnifiedButton>
      )}

      {/* App container - this slides to reveal coach */}
      <div
        className={cn(
          'app-container h-full w-full transition-transform duration-300 ease-in-out',
          'transform-gpu will-change-transform backface-hidden'
        )}
        style={{
          transform: isCoachOpen ? `translate3d(-${slideDistance}, 0, 0)` : 'translate3d(0, 0, 0)',
          position: 'relative',
          overflow: 'hidden' // Important: contain the modal overlay
        }}
      >
        {/* Main app content */}
        <div className="relative h-full">
          {children}

          {/* Portal container for Radix UI modals - inside app content */}
          <div id="radix-portal-container" />
        </div>

        {/* Darkening overlay - over everything including modals */}
        <div
          className={cn(
            'absolute inset-0 z-60 transition-all duration-300',
            'pointer-events-none opacity-0',
            isCoachOpen && 'pointer-events-auto opacity-100'
          )}
          style={{
            backgroundColor: isCoachOpen ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
            backdropFilter: isCoachOpen ? 'blur(2px)' : 'none'
          }}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            setIsCoachOpen(false)
          }}
          onMouseDown={(e) => {
            e.stopPropagation()
            e.preventDefault()
          }}
          onPointerDown={(e) => {
            e.stopPropagation()
            e.preventDefault()
          }}
        />
      </div>

      {/* Coach panel - fixed position outside the sliding container */}
      {isAICoachEnabled && (
        <div
          className={cn(
            'coach-panel bg-background fixed top-0 right-0 h-full z-[70]',
            'border-l transition-transform duration-300 ease-in-out',
            'transform-gpu will-change-transform'
          )}
          style={{
            width: slideDistance,
            transform: isCoachOpen ? 'translate3d(0, 0, 0)' : `translate3d(${slideDistance}, 0, 0)`
          }}
        >
          {/* Subtle cool-toned glow centered on the edge */}
          <div
            className={cn(
              'absolute -left-24 w-48 transition-all duration-700 pointer-events-none',
              isCoachOpen ? 'opacity-100' : 'opacity-0'
            )}
            style={{
              top: '-10%',
              height: '120%',
              background: `linear-gradient(to top,
                rgba(239, 68, 68, 0.08) 0%,       /* red-500 */
                rgba(236, 72, 153, 0.07) 25%,     /* pink-500 */
                rgba(14, 165, 233, 0.06) 50%,     /* sky-500 */
                rgba(99, 102, 241, 0.05) 75%,     /* indigo-500 */
                rgba(168, 85, 247, 0.04) 100%     /* purple-500 */
              )`,
              filter: 'blur(60px)',
              zIndex: -1
            }}
          />

          <CoachSidebar isOpen={isCoachOpen} setIsOpen={setIsCoachOpen} />
        </div>
      )}
    </div>
  )
}
